import React from 'react';
import { motion } from 'framer-motion';
import {
    Sprout,
    ArrowRight,
    ChevronRight,
    TreePine,
    GraduationCap,
    Music,
    ShieldCheck
} from 'lucide-react';
import tamil from '../assets/Tamil.jpeg';
import musical from '../assets/Musical.jpeg';

export default function CompletedProjects() {
    const darkGreen = "#1A2E20";
    const goldAccent = "#D4AF37";
    const creamBg = "#FDFBF7";
    const bgPattern = "https://www.transparenttextures.com/patterns/natural-paper.png";

    const fadeIn = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-50px" },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    const completedInitiatives = [
        {
            title: "Green Project Kitulgala",
            category: "Carbon Zero Vision - 2017",
            description: "Our first step toward environmental harmony. We planted 200 native trees—bamboo, kumbuk, mee, and diyapara—with the support of local villagers to heal the planet we share.",
            image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?q=80&w=1000",
            icon: <TreePine size={24} className="text-[#B89561]" />
        },
        {
            title: "School Supply Drive",
            category: "Education Support - 2024",
            description: "On Sri Lanka’s Independence Day, we distributed essential supplies to Tamil children from low-income families in Elephant Pass, supported by global donors.",
            image: tamil,
            icon: <GraduationCap size={24} className="text-[#B89561]" />
        },
        {
            title: "Ragama Sevana Victory Day",
            category: "Military Support - 2023",
            description: "Providing much-needed musical instruments for the Ranaviru Sevana band. We witnessed the incredible power of music and determination in our wounded soldiers.",
            image: musical,
            icon: <Music size={24} className="text-[#B89561]" />
        }
    ];

    return (
        <div style={{ backgroundColor: creamBg, color: darkGreen }} className="min-h-screen font-sans selection:bg-[#B89561] selection:text-white relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none opacity-40 z-10" style={{ backgroundImage: `url(${bgPattern})` }}></div>

            {/* 1. HERO SECTION */}
            <section className="relative h-[60vh] md:h-[65vh] flex items-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=2000" className="w-full h-full object-cover" alt="Completed Milestones" />
                    <div className="absolute inset-0 bg-[#1A2E20]/50"></div>
                </div>

                <div className="relative z-20 max-w-7xl mx-auto px-6 w-full pt-20">
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                        <div className="flex items-center gap-2 text-white/90 text-[10px] tracking-[0.4em] uppercase mb-6 font-bold">
                            <span>Projects</span>
                            <ChevronRight size={12} className="text-[#B89561]" />
                            <span className="text-white font-medium">Completed Milestones</span>
                        </div>
                        <h1 className="font-serif text-5xl md:text-8xl text-white leading-tight">
                            Archives of <br/>
                            <span className="italic font-light text-[#B89561]">Goodwill</span>
                        </h1>
                    </motion.div>
                </div>

                {/*<div className="absolute bottom-[-10px] right-[-10px] z-10 pointer-events-none">*/}
                {/*    <h2 className="text-white/10 text-[10rem] md:text-[14rem] font-serif leading-none uppercase select-none">Impact</h2>*/}
                {/*</div>*/}
            </section>

            {/* 2. INTRO SECTION */}
            <section className="relative py-24 px-6 z-20">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div {...fadeIn}>
                        <div className="flex justify-center mb-8">
                            <ShieldCheck color={goldAccent} size={48} />
                        </div>
                        <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.4em] text-[11px] block mb-6">A Legacy of Service</span>
                        <h2 className="font-serif text-4xl md:text-6xl text-[#1A2E20] mb-8 leading-tight">Living Symbols of Boseth’s Promise</h2>
                        <p className="text-gray-600 text-xl font-light leading-relaxed">
                            Each completed project is a landmark in our mission to heal not only people—but the environment and the spirit of our community. From our first tree planting to today, these milestones represent the power of collective compassion.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* 3. COMPLETED INITIATIVES GRID */}
            <section className="relative pb-32 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
                        {completedInitiatives.map((item, i) => (
                            <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }} className="group">
                                <div className="relative aspect-square overflow-hidden rounded-[2.5rem] mb-8 shadow-xl">
                                    <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                                    <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2">
                                        {item.icon}
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#1A2E20]">{item.category}</span>
                                    </div>
                                </div>
                                <div className="px-4 text-center md:text-left">
                                    <h3 className="font-serif text-3xl text-[#1A2E20] mb-4 group-hover:text-[#B89561] transition-colors">
                                        {item.title}
                                    </h3>
                                    <p className="text-gray-500 text-base font-light leading-relaxed">
                                        {item.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 4. FOOTER NOTE */}
            <section className="relative py-24 bg-[#1A2E20] text-white">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <motion.div {...fadeIn}>
                        <Sprout color={goldAccent} size={32} className="mx-auto mb-8" />
                        <h2 className="font-serif text-4xl mb-6 italic font-light">"The trees we plant today give life through the air we breathe tomorrow."</h2>
                        <div className="h-[1px] w-24 bg-[#B89561]/30 mx-auto"></div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}